// Modal "Nova / Editar frase gatilho" (mockup "Modal Frase Gatilho A"):
// cabeçalho com toggle Ativa, nome, grid Fluxo + Conexões (chips), lista de
// frases com segmentado Exata/Parcial, aviso de conflito com outra frase na
// mesma conexão (regra pura em flowRules.phraseConflicts).
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { AccountTree, WarningAmber } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { phraseConflicts } from "../../pages/FlowBuilderConfig/flowRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    activeLabel: { fontSize: 12, fontWeight: 600, color: stateText(theme, AEROS.success, "success") },
    inactiveLabel: { color: theme.palette.text.secondary },
    connChips: { display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" },
    connChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 6,
      padding: "3px 8px",
      background: "rgba(52,211,153,0.1)",
      color: stateText(theme, AEROS.success, "success"),
    },
    connChipOff: { background: "rgba(248,113,113,0.1)", color: stateText(theme, AEROS.error, "error") },
    dot: { width: 6, height: 6, borderRadius: "50%", background: "currentColor" },
    phraseRow: { display: "flex", gap: 8, alignItems: "center" },
    segmented: {
      display: "flex",
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      overflow: "hidden",
      fontSize: 11.5,
      fontWeight: 600,
      flex: "none",
    },
    segment: {
      padding: "9px 12px",
      color: theme.palette.text.secondary,
      background: "transparent",
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontWeight: 600,
      "& + &": { borderLeft: `1px solid ${borderInput}` },
      "&:hover": { color: theme.palette.text.primary },
    },
    segmentOn: {
      background: "rgba(160,68,255,0.2)",
      color: stateText(theme, "#C08BFF", "purple"),
      "&:hover": { color: stateText(theme, "#C08BFF", "purple") },
    },
    removeBtn: { padding: 4, color: theme.palette.aeros.textTertiary, "& .MuiSvgIcon-root": { fontSize: 18 }, "&:hover": { color: AEROS.error } },
    warn: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      background: theme.palette.aeros.surface,
      border: "1px solid rgba(251,191,36,0.3)",
      borderRadius: 10,
      fontSize: 12,
      color: theme.palette.text.secondary,
      "& b": { color: theme.palette.text.primary },
      "& .MuiSvgIcon-root": { fontSize: 16, color: stateText(theme, AEROS.warning, "warning"), flex: "none" },
    },
    menuItem: { fontSize: 13, gap: 6, "& .MuiCheckbox-root": { padding: 4 } },
    placeholder: { color: theme.palette.text.secondary },
    flowIcon: { fontSize: 16, color: stateText(theme, AEROS.cyan, "info"), marginRight: 6 },
  };
});

const emptyPhrase = () => ({ text: "", type: "exact" });

const CampaignModalPhrase = ({ open, onClose, FlowCampaignId, onSave, campaigns: campaignsProp }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(true);
  const [name, setName] = useState("");
  const [flowId, setFlowId] = useState("");
  const [whatsappIds, setWhatsappIds] = useState([]);
  const [phrases, setPhrases] = useState([emptyPhrase()]);
  const [flows, setFlows] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTouched(false);
    setActive(true);
    setName("");
    setFlowId("");
    setWhatsappIds([]);
    setPhrases([emptyPhrase()]);
    setLoading(true);
    const load = async () => {
      try {
        const [f, w, c] = await Promise.all([
          api.get("/flowbuilder"),
          api.get("/whatsapp"),
          campaignsProp ? Promise.resolve({ data: { flow: campaignsProp } }) : api.get("/flowcampaign"),
        ]);
        if (!alive) return;
        setFlows(f.data?.flows || []);
        setWhatsApps(Array.isArray(w.data) ? w.data : []);
        const list = Array.isArray(c.data?.flow) ? c.data.flow : Array.isArray(c.data) ? c.data : [];
        setCampaigns(list);
        if (FlowCampaignId) {
          const { data } = await api.get(`/flowcampaign/${FlowCampaignId}`);
          if (!alive) return;
          setName(data.name || "");
          setActive(data.status !== false);
          setFlowId(data.flowId || "");
          setWhatsappIds(Array.isArray(data.whatsappIds) ? data.whatsappIds : data.whatsappId ? [data.whatsappId] : []);
          const parsed = (Array.isArray(data.phrase) ? data.phrase : []).map((p) => ({
            text: p?.text ?? String(p ?? ""),
            type: p?.type === "partial" ? "partial" : "exact",
          }));
          setPhrases(parsed.length ? parsed : [emptyPhrase()]);
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [open, FlowCampaignId, campaignsProp]);

  const conflicts = useMemo(
    () => phraseConflicts(phrases, whatsappIds, campaigns, FlowCampaignId),
    [phrases, whatsappIds, campaigns, FlowCampaignId]
  );
  const connName = (id) => whatsApps.find((w) => w.id === id)?.name || `#${id}`;

  const validPhrases = phrases.filter((p) => p.text.trim());
  const errors = {
    name: !name.trim(),
    flow: !flowId,
    conns: whatsappIds.length === 0,
    phrases: validPhrases.length === 0,
  };
  const invalid = Object.values(errors).some(Boolean);

  const updatePhrase = (i, patch) =>
    setPhrases((old) => old.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  const handleSave = async () => {
    setTouched(true);
    if (invalid) {
      toast.error(i18n.t("aeros.flow.phraseModal.required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        flowId,
        phrases: validPhrases.map((p) => ({ text: p.text.trim(), type: p.type })),
        whatsappIds,
        status: active,
        ...(FlowCampaignId && { id: FlowCampaignId }),
      };
      if (FlowCampaignId) {
        await api.put("/flowcampaign", payload);
        toast.success(i18n.t("aeros.flow.phraseModal.updated"));
      } else {
        await api.post("/flowcampaign", payload);
        toast.success(i18n.t("aeros.flow.phraseModal.created"));
      }
      onSave && onSave();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      width={640}
      title={i18n.t(FlowCampaignId ? "aeros.flow.phraseModal.editTitle" : "aeros.flow.phraseModal.title")}
      subtitle={i18n.t("aeros.flow.phraseModal.subtitle")}
      headerRight={
        <>
          <span className={`${classes.activeLabel} ${active ? "" : classes.inactiveLabel}`}>
            {i18n.t(active ? "aeros.flow.phrases.active" : "aeros.flow.phrases.inactive")}
          </span>
          <Switch
            className={aeros.toggleSwitchSmall}
            color="primary"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            inputProps={{ "aria-label": i18n.t("aeros.flow.phrases.active") }}
          />
        </>
      }
      onSubmit={handleSave}
      loading={saving || loading}
      submitLabel={i18n.t(FlowCampaignId ? "aeros.flow.save" : "aeros.flow.create")}
    >
      <div>
        <label className={m.fieldLabel} htmlFor="phrase-name">{i18n.t("aeros.flow.phraseModal.name")}</label>
        <TextField
          id="phrase-name"
          variant="outlined"
          className={m.input}
          value={name}
          autoFocus
          placeholder={i18n.t("aeros.flow.phraseModal.namePlaceholder")}
          onChange={(e) => setName(e.target.value)}
          error={touched && errors.name}
        />
      </div>

      <div className={m.grid2} style={{ gap: 12 }}>
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.flow.phraseModal.flow")}</label>
          <TextField
            select
            variant="outlined"
            className={m.input}
            value={flowId}
            onChange={(e) => setFlowId(e.target.value)}
            error={touched && errors.flow}
            SelectProps={{
              displayEmpty: true,
              renderValue: (v) => {
                const f = flows.find((x) => x.id === v);
                return (
                  <span style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                    <AccountTree className={classes.flowIcon} />
                    <span className={f ? "" : classes.placeholder} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f ? f.name : i18n.t("aeros.flow.phraseModal.flowPlaceholder")}
                    </span>
                  </span>
                );
              },
            }}
          >
            {flows.map((f) => (
              <MenuItem key={f.id} value={f.id} className={classes.menuItem}>{f.name}</MenuItem>
            ))}
          </TextField>
        </div>
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.flow.phraseModal.connections")}</label>
          <TextField
            select
            variant="outlined"
            className={m.input}
            value={whatsappIds}
            onChange={(e) => setWhatsappIds(e.target.value)}
            error={touched && errors.conns}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (sel) =>
                sel.length === 0 ? (
                  <span className={classes.placeholder}>{i18n.t("aeros.flow.phraseModal.connectionsPlaceholder")}</span>
                ) : (
                  <span className={classes.connChips}>
                    {sel.map((id) => {
                      const w = whatsApps.find((x) => x.id === id);
                      const on = w?.status === "CONNECTED";
                      return (
                        <span key={id} className={`${classes.connChip} ${on ? "" : classes.connChipOff}`}>
                          <span className={classes.dot} />
                          {w?.name || `#${id}`}
                        </span>
                      );
                    })}
                  </span>
                ),
            }}
          >
            {whatsApps.map((w) => (
              <MenuItem key={w.id} value={w.id} className={classes.menuItem}>
                <Checkbox size="small" color="primary" checked={whatsappIds.includes(w.id)} />
                <span className={classes.dot} style={{ color: w.status === "CONNECTED" ? AEROS.success : AEROS.error }} />
                {w.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
          <label className={m.fieldLabel} style={{ marginBottom: 0 }}>{i18n.t("aeros.flow.phraseModal.phrases")}</label>
          <button type="button" className={m.linkButton} onClick={() => setPhrases((old) => [...old, emptyPhrase()])}>
            <AddIcon /> {i18n.t("aeros.flow.phraseModal.addPhrase")}
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {phrases.map((p, i) => (
            <div key={i} className={classes.phraseRow}>
              <TextField
                variant="outlined"
                className={m.input}
                style={{ flex: 1 }}
                value={p.text}
                placeholder={i18n.t("aeros.flow.phraseModal.phrasePlaceholder")}
                onChange={(e) => updatePhrase(i, { text: e.target.value })}
                error={touched && errors.phrases && !p.text.trim()}
              />
              <div className={classes.segmented} role="tablist">
                {["exact", "partial"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={p.type === t}
                    className={`${classes.segment} ${p.type === t ? classes.segmentOn : ""}`}
                    onClick={() => updatePhrase(i, { type: t })}
                  >
                    {i18n.t(`aeros.flow.phrases.${t}`)}
                  </button>
                ))}
              </div>
              <IconButton
                className={classes.removeBtn}
                aria-label={i18n.t("aeros.flow.phraseModal.removePhrase")}
                disabled={phrases.length === 1}
                onClick={() => setPhrases((old) => old.filter((_, idx) => idx !== i))}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </div>
          ))}
        </div>
        <div className={m.hint}>
          <InfoOutlinedIcon /> {i18n.t("aeros.flow.phraseModal.hint")}
        </div>
      </div>

      {conflicts.slice(0, 2).map((c) => (
        <div key={`${c.text}-${c.campaignName}`} className={classes.warn}>
          <WarningAmber />
          <span>
            {i18n.t("aeros.flow.phraseModal.conflictA", { text: c.text })} <b>{c.campaignName}</b>{" "}
            {i18n.t("aeros.flow.phraseModal.conflictB", { connection: connName(c.whatsappId) })}
          </span>
        </div>
      ))}
    </AerosDialog>
  );
};

export default CampaignModalPhrase;
